import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../styles/landing.css";

import HeroScrub from "../../components/landing/heroScrub";
import {
  Header,
  Ticker,
  Blends,
  Crema,
  Heritage,
  Why,
  B2B,
  Faq,
  Contact,
  Footer,
} from "../../components/landing/sections";
import { usePauseOnHide } from "../../components/landing/motion";
import FloatingChatbot from "../../components/landing/chatbot";
import contactService from "../../services/api/contactService";

const FAQ_JSONLD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    [
      "Hvad er minimumsordren?",
      "Ingen fast minimumsordre. Som Starter begynder du fra blot 5 kg/md. Jo mere du bestiller, jo bedre vilkår og priser.",
    ],
    [
      "Hvor hurtigt leveres der til Danmark?",
      "Sjælland og Fyn: 1-2 hverdage. Jylland: 2-3 hverdage. Ordrer modtaget inden kl. 12 afsender vi samme dag.",
    ],
    [
      "Tilbyder I prøvesmagning?",
      "Ja. Nye samarbejdspartnere kan få tilsendt smagsprøver, så I kan teste kvaliteten før I bestiller større mængder.",
    ],
    [
      "Kan vi ændre vores ordre fra måned til måned?",
      "Ja. Du kan justere mængder eller produkter løbende, så det passer til dit faktiske forbrug.",
    ],
    [
      "Hvilke kaffetyper tilbyder I?",
      "Vi tilbyder både Arabica, Robusta og forskellige blends, så du kan vælge den smagsprofil der passer bedst til din virksomhed eller café.",
    ],
    [
      "Tilbyder I kaffe til kontorer og virksomheder?",
      "Ja. Vi leverer kaffe til både kontorer, caféer, restauranter og detailbutikker.",
    ],
    [
      "Kan I hjælpe med valg af kaffe?",
      "Ja. Vi rådgiver gerne om hvilken kaffe og ristning der passer bedst til jeres maskiner og kunder.",
    ],
  ].map(([name, text]) => ({
    "@type": "Question",
    name,
    acceptedAnswer: { "@type": "Answer", text },
  })),
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  usePauseOnHide();

  /* Arriving from the catalogue with a section in mind. Runs after
     ScrollToTop has jumped to the top, so the anchor wins. */
  useEffect(() => {
    if (!state?.scrollTo) return;
    const id = state.scrollTo;
    const raf = requestAnimationFrame(() =>
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }),
    );
    return () => cancelAnimationFrame(raf);
  }, [state]);

  return (
    <>
      <Helmet>
        <title>
          Amigos Caffè Danmark | Scandivia Food Group - Italiensk kaffe til
          erhverv
        </title>
        <meta
          name="description"
          content="Officiel dansk distributør af Amigos Caffè, ristet i Trieste siden 1980. Premium italiensk espresso til caféer, restauranter, hoteller og kontorer. Ingen minimumsordre, levering 1-3 hverdage."
        />
        <link rel="canonical" href="https://www.scandiviafood.dk/" />
        <meta name="robots" content="index, follow" />
        <meta name="theme-color" content="#0b1a12" />
        <meta
          property="og:title"
          content="Amigos Caffè Danmark | Scandivia Food Group"
        />
        <meta
          property="og:description"
          content="Ægte italiensk espresso, ristet i Trieste siden 1980. Leveret i Danmark til caféer, restauranter, hoteller og kontorer."
        />
        <meta
          property="og:image"
          content="https://www.scandiviafood.dk/assets/hero/hero-ending.jpg"
        />
        <script type="application/ld+json">{JSON.stringify(FAQ_JSONLD)}</script>
      </Helmet>

      <a className="skip-link" href="#main">
        Spring til indhold
      </a>
      <div className="env" aria-hidden="true" />

      <Header
        onLogin={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />

      <main id="main" tabIndex={-1}>
        <HeroScrub />
        <Ticker />
        <Blends onCatalog={() => navigate("/products")} />
        <Crema />
        <Heritage />
        <Why />
        <B2B />
        <Faq />
        <Contact submit={(form) => contactService.createContacts(form)} />
      </main>

      <FloatingChatbot />
      <Footer Link={Link} />
    </>
  );
}
